import { useState, useEffect, useRef } from "react";
import {
  Camera,
  Check,
  ArrowRight,
  Mail,
  MapPin,
  Send,
  Cookie,
  AlertCircle,
} from "lucide-react";
import {
  products as defaultCatalog,
  type Product,
  type Store,
  type SearchFilters,
  type SavedSearch,
} from "@little-finds/core";
import {
  api,
  demo,
  configured,
  supabase,
  uploadPhoto,
  preparePhoto,
  rpc,
  RpcError,
} from "../api";
import {
  draftKeyFor,
  restoreDraft,
  draftVisit,
  readDraftPhoto,
  saveDraftPhoto,
  type SightingDraft,
} from "../sightingDraft";
import { ProductArt } from "./ProductArt";
import { Buddy } from "./Buddy";
import { emailSignInEnabled } from "../readiness";
import { searchAreaSummary, wishlistSummary } from "../wishlist";
export function LoginForm({
  onDone,
  onBrowse,
}: {
  onDone: () => void;
  onBrowse?: () => void;
}) {
  const [email, setEmail] = useState(""),
    [code, setCode] = useState(""),
    [sent, setSent] = useState(false),
    [busy, setBusy] = useState(false),
    [error, setError] = useState("");
  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      if (!supabase)
        throw new Error(
          "Account sign-in will be available when the shared service is connected. The demo is available now.",
        );
      const { error } = sent
        ? await supabase.auth.verifyOtp({ email, token: code, type: "email" })
        : await supabase.auth.signInWithOtp({
            email,
            options: { shouldCreateUser: true },
          });
      if (error) throw error;
      if (sent) onDone();
      else setSent(true);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  if (!emailSignInEnabled)
    return (
      <div className="stack sign-in-unavailable">
        <Buddy compact />
        <h3>Email sign-in isn’t open yet</h3>
        <p className="muted">
          You can browse without an account. Your unsent visit stays on this
          device until sign-in is ready.
        </p>
        <button
          className="button secondary"
          type="button"
          onClick={onBrowse ?? onDone}
        >
          Keep browsing
        </button>
        <a className="text-button" href="?demo=1">
          Try the clearly labeled demo <ArrowRight size={16} />
        </a>
      </div>
    );
  return (
    <form onSubmit={submit} className="stack">
      <div className="login-art">
        <Buddy compact />
      </div>
      <p className="muted">
        A little account for your little finds. Save your wishlist, share a
        sighting, and give a good dog a treat.
      </p>
      <label>
        Email address
        <input
          type="email"
          required
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={sent}
          placeholder="you@example.com"
        />
      </label>
      {sent && (
        <label>
          Check your inbox for a six-digit code
          <input
            inputMode="numeric"
            pattern="[0-9]{6}"
            autoComplete="one-time-code"
            required
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="000000"
          />
        </label>
      )}
      {error && (
        <p role="alert" className="form-error">
          {error}
        </p>
      )}
      <button className="button" disabled={busy || !configured}>
        <Mail size={17} />
        {busy ? "One moment…" : sent ? "You’re in" : "Email me a code"}
      </button>
      {sent && (
        <button
          type="button"
          className="text-button"
          onClick={() => setSent(false)}
        >
          Use a different email
        </button>
      )}
      {!configured && (
        <a className="button secondary" href="?demo=1">
          Try the demo <ArrowRight size={17} />
        </a>
      )}
      <p className="tiny muted">
        By continuing, you agree to the community guidelines. Your visits are
        shared at store level; we never track your journey.
      </p>
    </form>
  );
}
export function SightingForm({
  stores,
  catalog = defaultCatalog,
  draftOwner,
  initialStore,
  onDone,
}: {
  stores: Store[];
  catalog?: Product[];
  draftOwner: string;
  initialStore?: string;
  onDone: () => void;
}) {
  const draftKey = draftKeyFor(draftOwner, demo);
  const [draft, setDraft] = useState<SightingDraft>(() => {
    let raw: string | null = null;
    try {
      raw = localStorage.getItem(draftKey);
    } catch {
      /* Storage can be disabled. */
    }
    const previous = restoreDraft(raw, crypto.randomUUID());
    // A restored draft keeps its branch; opening another card must not move the visit.
    return {
      ...previous,
      store: previous.store || initialStore || "",
    };
  });
  const [restored, setRestored] = useState(() => {
    try {
      return Boolean(localStorage.getItem(draftKey));
    } catch {
      return false;
    }
  });
  const [resetConfirm, setResetConfirm] = useState(false);
  const [productQuery, setProductQuery] = useState("");
  const shopsQuery = useRef("");
  const [choices, setChoices] = useState(stores),
    [shopQuery, setShopQuery] = useState("");
  const [moreShops, setMoreShops] = useState(false),
    [shopOffset, setShopOffset] = useState(0),
    [loadingShops, setLoadingShops] = useState(false);
  const [photo, setPhoto] = useState<File | null>(null),
    [photoReady, setPhotoReady] = useState(false);
  const [error, setError] = useState(""),
    [storageWarning, setStorageWarning] = useState("");
  const [busy, setBusy] = useState(false),
    [preparingPhoto, setPreparingPhoto] = useState(false);
  const [done, setDone] = useState(false),
    [approved, setApproved] = useState(false);
  const locked = busy || preparingPhoto || Boolean(draft.pending_visit);

  useEffect(() => {
    shopsQuery.current = shopQuery;
    let active = true;
    const timer = setTimeout(() => {
      setLoadingShops(true);
      api
        .storePage(shopQuery)
        .then((page) => {
          if (active) {
            setChoices((previous) => [
              ...page.stores,
              ...previous.filter(
                (s) =>
                  s.id === draft.store &&
                  !page.stores.some((r) => r.id === s.id),
              ),
            ]);
            setMoreShops(page.has_more);
            setShopOffset(page.stores.length);
          }
        })
        .catch((e) => {
          if (active)
            setError(`Shops could not be loaded. ${(e as Error).message}`);
        })
        .finally(() => {
          if (active) setLoadingShops(false);
        });
    }, 200);
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [shopQuery]);
  useEffect(() => {
    if (!draft.store || choices.some((s) => s.id === draft.store)) return;
    let active = true;
    api
      .store(draft.store)
      .then((store) => {
        if (!active) return;
        if (store)
          setChoices((rows) =>
            rows.some((s) => s.id === store.id) ? rows : [...rows, store],
          );
        else
          setError(
            "The shop saved in this draft is no longer listed. Choose another verified shop before sending.",
          );
      })
      .catch((e) => {
        if (active)
          setError(
            `The saved shop could not be checked. ${(e as Error).message}`,
          );
      });
    return () => {
      active = false;
    };
  }, [draft.store]);
  useEffect(() => {
    let active = true;
    readDraftPhoto(draftKey)
      .then((file) => {
        if (active) setPhoto(file);
      })
      .catch(() => {
        if (active && draft.photo_name && !draft.photo_path)
          setStorageWarning(
            "Reattach your shelf photo before sending; local photo storage is unavailable.",
          );
      })
      .finally(() => {
        if (active) setPhotoReady(true);
      });
    return () => {
      active = false;
    };
  }, [draftKey]);
  useEffect(() => {
    if (done) return;
    try {
      localStorage.setItem(draftKey, JSON.stringify(draft));
    } catch {
      setStorageWarning(
        "This browser cannot save your draft. Keep this tab open until it is sent.",
      );
    }
  }, [draft, draftKey, done]);

  async function resetDraft() {
    setBusy(true);
    await saveDraftPhoto(draftKey, null).catch(() => {});
    const empty = restoreDraft(null, crypto.randomUUID());
    setDraft({ ...empty, store: initialStore ?? "" });
    setPhoto(null);
    setRestored(false);
    setResetConfirm(false);
    setError("");
    setStorageWarning("");
    setProductQuery("");
    setBusy(false);
  }
  async function choosePhoto(file: File | null) {
    setError("");
    setPreparingPhoto(true);
    try {
      const prepared = file
        ? new File([await preparePhoto(file)], "shelf-photo.jpg", {
            type: "image/jpeg",
          })
        : null;
      try {
        await saveDraftPhoto(draftKey, prepared);
      } catch {
        setStorageWarning(
          "Your photo is ready in this tab, but could not be saved for later. Keep this tab open until it is sent.",
        );
      }
      setPhoto(prepared);
      setDraft((d) => ({
        ...d,
        photo_path: undefined,
        photo_name: prepared?.name,
        request_id: crypto.randomUUID(),
      }));
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setPreparingPhoto(false);
    }
  }
  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      if (!demo) {
        const { data, error } = await supabase!.auth.getUser();
        if (error || data.user?.id !== draftOwner)
          throw new Error(
            "Your account changed. Close this form and sign in again before sending.",
          );
      }
      let visit = draft.pending_visit;
      if (!visit) {
        const base = draftVisit(draft);
        if (!draft.photo_path && draft.photo_name && !photo)
          throw new Error(
            "Reattach your shelf photo before sending, or remove it from this draft.",
          );
        const path =
          draft.photo_path ??
          (photo ? await uploadPhoto(photo, draft.request_id) : undefined);
        visit = { ...base, photo_path: path };
        const pending = { ...draft, photo_path: path, pending_visit: visit };
        // Preserve the exact request until confirmed so a network retry cannot change its observations.
        try {
          localStorage.setItem(draftKey, JSON.stringify(pending));
        } catch {
          /* Current tab retains it. */
        }
        setDraft(pending);
      }
      const receipt = await api.submit(visit);
      setApproved(receipt.status === "approved");
      setDone(true);
      try {
        localStorage.removeItem(draftKey);
      } catch {
        /* Already confirmed by server. */
      }
      await saveDraftPhoto(draftKey, null).catch(() => {});
      onDone();
    } catch (e) {
      // A database rejection is definitive: the transaction did not commit.
      // A transport failure keeps the exact payload locked for a safe retry.
      if (
        e instanceof RpcError &&
        ["P0001", "22P02", "22007", "22008", "23503", "23514"].includes(e.code)
      )
        setDraft((d) => ({ ...d, pending_visit: undefined }));
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  if (done)
    return (
      <div className="success-state" role="status">
        <span className="success-icon">
          <Cookie size={30} />
        </span>
        <h3>
          {approved
            ? "A helpful visit. A happy little community."
            : "One good deed. One pending biscuit."}
        </h3>
        <p>
          {approved
            ? "Your structured observations are published. Any photo or note still waits for review. Your treat jar reflects eligible visits."
            : "Your sighting is in the review basket. Once it’s approved, it helps another collector — and an eligible treat lands in your jar."}
        </p>
        {demo && (
          <p className="demo-note">
            Demo only. Try “Review demo” to approve this sample.
          </p>
        )}
      </div>
    );
  return (
    <form onSubmit={submit} className="stack">
      <p className="muted">
        A tiny tip can make someone’s day. Share a separate result for each
        product from the same shop visit.
      </p>
      {restored && (
        <aside className="draft-notice">
          <strong>Your unsent visit is here.</strong>
          <p>
            {choices.find((s) => s.id === draft.store)?.name ??
              "The shop you chose"}{" "}
            · Saved only on this device.
          </p>
          {initialStore && initialStore !== draft.store && (
            <p>
              You opened a different shop. This draft still belongs to the shop
              above.
            </p>
          )}
          {!draft.pending_visit && (
            <button
              type="button"
              className="text-button"
              disabled={busy || !photoReady}
              onClick={() => setResetConfirm(true)}
            >
              Start a new visit
            </button>
          )}
        </aside>
      )}
      {resetConfirm && (
        <div
          className="draft-notice"
          role="group"
          aria-label="Clear the unsent draft"
        >
          <p>Clear this device’s unsent visit and attached photo?</p>
          <button
            type="button"
            className="button secondary small"
            onClick={() => void resetDraft()}
          >
            Clear draft and start fresh
          </button>
          <button
            type="button"
            className="text-button"
            onClick={() => setResetConfirm(false)}
          >
            Keep my draft
          </button>
        </div>
      )}
      <fieldset className="sighting-fields" disabled={locked}>
        <label>
          Search for a shop
          <input
            aria-label="Search shops for sighting"
            value={shopQuery}
            onChange={(e) => setShopQuery(e.target.value)}
            placeholder="Shop name, city, or ZIP"
          />
        </label>
        <label>
          Which shop?
          <select
            required
            value={draft.store}
            onChange={(e) => setDraft({ ...draft, store: e.target.value })}
          >
            <option value="">Choose a shop</option>
            {choices.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name} · {s.address} · {s.city}, {s.state}
              </option>
            ))}
          </select>
        </label>
        {moreShops && (
          <button
            type="button"
            className="text-button"
            disabled={loadingShops}
            onClick={async () => {
              setLoadingShops(true);
              try {
                const page = await api.storePage(shopQuery, shopOffset);
                setChoices((rows) => [
                  ...rows,
                  ...page.stores.filter(
                    (s) => !rows.some((v) => v.id === s.id),
                  ),
                ]);
                setShopOffset((offset) => offset + page.stores.length);
                setMoreShops(page.has_more);
              } catch (e) {
                setError((e as Error).message);
              } finally {
                setLoadingShops(false);
              }
            }}
          >
            {loadingShops ? "Loading shops…" : "Load more shops"}
          </button>
        )}
        {!choices.length && (
          <p className="form-error">
            No verified shops are loaded here yet. Suggest a shop from the map
            first.
          </p>
        )}
        <fieldset>
          <legend>
            Which products did you check?{" "}
            <span className="muted">{draft.observations.length}/20</span>
          </legend>
          <label className="sighting-product-search">
            Find a product
            <input
              type="search"
              value={productQuery}
              onChange={(e) => setProductQuery(e.target.value)}
              placeholder="Product name or brand"
            />
          </label>
          <div className="product-picker">
            {catalog
              .filter(
                (p) =>
                  p.category !== "bag" &&
                  `${p.brand} ${p.name}`
                    .toLowerCase()
                    .includes(productQuery.trim().toLowerCase()),
              )
              .map((p) => {
                const selected = draft.observations.some(
                  (o) => o.product_id === p.id,
                );
                return (
                  <button
                    key={p.id}
                    type="button"
                    className={selected ? "picked" : ""}
                    aria-pressed={selected}
                    disabled={!selected && draft.observations.length >= 20}
                    onClick={() =>
                      setDraft({
                        ...draft,
                        observations: selected
                          ? draft.observations.filter(
                              (o) => o.product_id !== p.id,
                            )
                          : [
                              ...draft.observations,
                              {
                                product_id: p.id,
                                outcome: "",
                                variant: "",
                              },
                            ],
                      })
                    }
                  >
                    <ProductArt product={p} size={30} />
                    {p.name}
                    {selected && <Check size={15} />}
                  </button>
                );
              })}
          </div>
          {!catalog.some(
            (p) =>
              p.category !== "bag" &&
              `${p.brand} ${p.name}`
                .toLowerCase()
                .includes(productQuery.trim().toLowerCase()),
          ) && (
            <p className="muted">No matching product. Try its name or brand.</p>
          )}
        </fieldset>
        {draft.observations.map((o) => {
          const product = catalog.find((p) => p.id === o.product_id);
          return (
            <fieldset className="observation-card" key={o.product_id}>
              <legend>{product?.name ?? "Previously selected product"}</legend>
              {!product && (
                <p className="form-error">
                  This product is no longer in the current catalog. Remove it
                  before sending.
                </p>
              )}
              <div className="form-grid">
                <label>
                  What happened?
                  <select
                    aria-label={`Result for ${product?.name ?? o.product_id}`}
                    required
                    value={o.outcome}
                    onChange={(e) =>
                      setDraft({
                        ...draft,
                        observations: draft.observations.map((v) =>
                          v.product_id === o.product_id
                            ? {
                                ...v,
                                outcome: e.target.value as typeof o.outcome,
                              }
                            : v,
                        ),
                      })
                    }
                  >
                    <option value="">Choose what happened</option>
                    <option value="saw_it">Saw it</option>
                    <option value="couldnt_find">Couldn’t find it</option>
                    <option value="staff_unavailable">
                      Staff said unavailable
                    </option>
                  </select>
                </label>
                <label>
                  Size or color <span className="muted">(optional)</span>
                  <input
                    aria-label={`Size or color for ${product?.name ?? o.product_id}`}
                    value={o.variant}
                    maxLength={100}
                    placeholder="e.g. Original size, blue"
                    onChange={(e) =>
                      setDraft({
                        ...draft,
                        observations: draft.observations.map((v) =>
                          v.product_id === o.product_id
                            ? { ...v, variant: e.target.value }
                            : v,
                        ),
                      })
                    }
                  />
                </label>
              </div>
              <button
                type="button"
                className="text-button"
                onClick={() =>
                  setDraft({
                    ...draft,
                    observations: draft.observations.filter(
                      (v) => v.product_id !== o.product_id,
                    ),
                  })
                }
              >
                Remove {product?.name ?? "product"}
              </button>
            </fieldset>
          );
        })}
        <label>
          When did you visit?
          <input
            type="datetime-local"
            required
            value={draft.date}
            onChange={(e) => setDraft({ ...draft, date: e.target.value })}
          />
        </label>
        <label>
          A little extra detail <span className="muted">(optional)</span>
          <textarea
            value={draft.note}
            maxLength={280}
            onChange={(e) => setDraft({ ...draft, note: e.target.value })}
            rows={2}
            placeholder="On the shelf near the checkout…"
          />
          <small className="muted">
            {draft.note.length}/280 · Keep it about the shop and product.
          </small>
        </label>
        <label className="upload">
          <Camera size={22} />
          <span>
            {photo?.name ??
              draft.photo_name ??
              (draft.photo_path ? "Shelf photo attached" : "Add a shelf photo")}
            <small>Optional · Location metadata is removed</small>
          </span>
          <input
            type="file"
            accept="image/jpeg,image/png,image/webp"
            onChange={(e) => {
              void choosePhoto(e.target.files?.[0] ?? null);
              e.target.value = "";
            }}
          />
        </label>
        {(photo || draft.photo_name || draft.photo_path) && (
          <button
            type="button"
            className="text-button"
            onClick={() => void choosePhoto(null)}
          >
            Remove shelf photo
          </button>
        )}
      </fieldset>
      {storageWarning && (
        <p role="status" className="form-error">
          {storageWarning}
        </p>
      )}
      {draft.pending_visit && (
        <p className="muted">
          This request is saved exactly as sent. Retry to confirm whether it
          arrived; it won’t create a second visit.
        </p>
      )}
      {error && (
        <p role="alert" className="form-error">
          {error}
          {!storageWarning && " Your draft is saved on this device."}
        </p>
      )}
      <button
        className="button"
        disabled={
          busy ||
          preparingPhoto ||
          !photoReady ||
          draft.observations.some(
            (o) => !catalog.some((p) => p.id === o.product_id),
          )
        }
      >
        <Send size={17} />
        {busy
          ? "Sending your sighting…"
          : preparingPhoto
            ? "Preparing photo…"
            : draft.pending_visit
              ? "Retry this sighting"
              : "Share a little find"}
      </button>
      <p className="tiny muted">
        Your draft saves on this device as you go. Reports are reviewed before
        sharing. Helpful “not found” reports earn treats too.
      </p>
    </form>
  );
}
export function SaveSearchForm({
  filters,
  catalog = defaultCatalog,
  existing,
  onDone,
}: {
  filters: SearchFilters;
  catalog?: Product[];
  existing?: SavedSearch;
  onDone: (saved: SavedSearch) => void;
}) {
  const [name, setName] = useState(existing?.name ?? "My little wishlist"),
    [alerts, setAlerts] = useState(existing?.alerts_enabled ?? true),
    [busy, setBusy] = useState(false),
    [error, setError] = useState("");
  return (
    <form
      className="stack"
      onSubmit={async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
          const saved = await api.save({
            id: existing?.id,
            name,
            filters,
            alerts_enabled: alerts,
          });
          onDone(saved);
        } catch (e) {
          setError((e as Error).message);
        } finally {
          setBusy(false);
        }
      }}
    >
      <p className="muted">
        Keep these products and this search area together. We’ll remember them
        on your phone, too.
      </p>
      <label>
        Give this hunt a name
        <input
          value={name}
          maxLength={80}
          required
          onChange={(e) => setName(e.target.value)}
        />
      </label>
      <div className="saved-summary">
        <MapPin size={20} />
        <span>
          {searchAreaSummary(filters)}
          <br />
          <small>{wishlistSummary(filters, catalog).detail}</small>
        </span>
      </div>
      <label className="check-row">
        <input
          type="checkbox"
          checked={alerts}
          onChange={(e) => setAlerts(e.target.checked)}
        />
        <span>
          Tell me when there’s a new sighting
          <small>
            In your inbox, and on iPhone when notifications are enabled.
          </small>
        </span>
      </label>
      {error && (
        <p className="form-error" role="alert">
          {error}
        </p>
      )}
      <button className="button" disabled={busy}>
        {busy
          ? "Saving…"
          : existing
            ? "Save hunt changes"
            : "Save this little hunt"}
        <Check size={17} />
      </button>
    </form>
  );
}
export function SuggestStoreForm({ onDone }: { onDone: () => void }) {
  const [v, setV] = useState({
      name: "",
      address: "",
      city: "",
      state: "",
      zip: "",
      website: "",
    }),
    [error, setError] = useState(""),
    [done, setDone] = useState(false);
  if (done)
    return (
      <div className="success-state">
        <MapPin size={40} />
        <h3>Thanks for putting it on our radar.</h3>
        <p>We’ll verify the shop’s location before adding it to the map.</p>
      </div>
    );
  return (
    <form
      className="stack"
      onSubmit={async (e) => {
        e.preventDefault();
        try {
          if (!demo) await rpc("suggest_store", { p_store: v });
          setDone(true);
          onDone();
        } catch (e) {
          setError((e as Error).message);
        }
      }}
    >
      <p className="muted">Know a lovely little shop we’re missing?</p>
      {Object.entries(v).map(([k, value]) => (
        <label key={k} className="capitalize">
          {k}
          <input
            value={value}
            required={k !== "website"}
            maxLength={200}
            onChange={(e) => setV({ ...v, [k]: e.target.value })}
          />
        </label>
      ))}
      {error && (
        <p role="alert" className="form-error">
          {error}
        </p>
      )}
      <button className="button">
        <MapPin size={17} />
        Suggest this shop
      </button>
      {demo && (
        <small className="demo-note">
          This demo suggestion won’t be sent anywhere.
        </small>
      )}
    </form>
  );
}
